////
/// ============================================================================
/// COMPONENT / Drop Menu
/// ============================================================================
///
/// @link https://codyhouse.co/ds/components/app/drop-menu
/// @group components
////

import {easeOutQuart} from 'js-easing-functions';

export default () => {
    const DropMenu = function (element) {
        this.element = element;
        this.innerElement = this.element.querySelectorAll(".js-drop-menu-inner");
        this.trigger = document.querySelector("[aria-controls='drop-menu-id']");
        this.autocompleteInput = false;
        this.autocompleteList = false;
        this.animationDuration = parseFloat(getComputedStyle(this.element).getPropertyValue("--drop-menu-transition-duration")) || 0.3;
        // store some basic classes
        this.menuIsVisibleClass = "c-drop-menu--is-visible";
        this.menuLevelClass = "js-drop-menu-list";
        this.menuBtnInClass = "js-drop-menu-btn--sublist-control";
        this.menuBtnOutClass = "js-drop-menu-btn--back";
        this.levelInClass = "c-drop-menu_list--in";
        this.levelOutClass = "c-drop-menu_list--out";
        // store the max height of the element
        this.maxHeight = false;
        // store drop menu layout
        this.layout = false;
        // vertical gap - desktop layout
        this.verticalGap = parseInt(getComputedStyle(this.element).getPropertyValue("--drop-menu-gap-y")) || 0;
        // store autocomplete search results
        this.searchResults = [];
        // focusable elements
        this.focusableElements = [];

        initDropMenu(this);
    };

    function getChildrenByClassName(parentElement, className) {
        return Array.from(parentElement.children).filter((child) => {
            return child.classList.contains(className);
        });
    }

    function initDropMenu(menu) {
        // trigger drop menu opening/closing
        toggleDropMenu(menu);
        // toggle sublevels
        menu.element.addEventListener("click", function (event) {
            // check if we need to show a new sublevel
            let menuBtnIn = event.target.closest('.' + menu.menuBtnInClass);

            if (menuBtnIn) {
                showSubLevel(menu, menuBtnIn);
            }

            // check if we need to go back to a previous level
            let menuBtnOut = event.target.closest('.' + menu.menuBtnOutClass);

            if (menuBtnOut) {
                hideSubLevel(menu, menuBtnOut);
            }
        });

        // init automplete search
        // initAutocomplete(menu);
        // close drop menu on focus out
        initFocusOut(menu);
    }

    function toggleDropMenu(menu) { // toggle drop menu
        if (!menu.trigger) {
            return;
        }

        // actions to be performed when closing the drop menu
        menu.dropMenuClosed = function (event) {
            if (event.propertyName !== "visibility") {
                return;
            }

            if (getComputedStyle(menu.element).getPropertyValue("visibility") !== "hidden") {
                return;
            }

            menu.element.removeEventListener("transitionend", menu.dropMenuClosed);
            menu.element.removeAttribute("style");

            resetAllLevels(menu); // go back to main list
            resetAutocomplete(menu); // if autocomplte is enabled -> reset input fields
        };

        // click on trigger
        menu.trigger.addEventListener("click", function () {
            menu.element.removeEventListener("transitionend", menu.dropMenuClosed);

            let isVisible = dropMenuVisible(menu);

            menu.element.classList.toggle(menu.menuIsVisibleClass, !isVisible)

            if (isVisible) {
                menu.trigger.removeAttribute("aria-expanded")
                menu.element.addEventListener("transitionend", menu.dropMenuClosed);
            } else {
                menu.trigger.setAttribute("aria-expanded", true)
                menu.element.addEventListener("transitionend", function cb() {
                    menu.element.removeEventListener("transitionend", cb);
                    focusFirstElement(menu, menu.element);
                });

                getLayoutValue(menu);
                setDropMenuMaxHeight(menu); // set max-height
                placeDropMenu(menu); // desktop only
            }
        });
    }

    function dropMenuVisible(menu) {
        return menu.element.classList.contains(menu.menuIsVisibleClass);
    }

    function showSubLevel(menu, menuBtn) {
        let mainLevel = menuBtn.closest('.' + menu.menuLevelClass);
        let subLevel = getChildrenByClassName(menuBtn.parentNode, menu.menuLevelClass);

        if (!mainLevel || subLevel.length === 0) {
            return;
        }

        // trigger classes
        subLevel[0].classList.add(menu.levelInClass);
        mainLevel.classList.add(menu.levelOutClass);
        mainLevel.classList.remove(menu.levelInClass);

        // animate height of main element
        animateDropMenu(menu, mainLevel, subLevel[0], function () {
            focusFirstElement(menu, subLevel[0]);
        });
    }

    function hideSubLevel(menu, menuBtn) {
        let subLevel = menuBtn.closest('.' + menu.menuLevelClass);
        let mainLevel = subLevel.parentNode.closest('.' + menu.menuLevelClass);

        if (!mainLevel || !subLevel) {
            return;
        }

        // trigger classes
        subLevel.classList.remove(menu.levelInClass);
        mainLevel.classList.add(menu.levelInClass);
        mainLevel.classList.remove(menu.levelOutClass);

        // animate height of main element
        animateDropMenu(menu, subLevel, mainLevel, function () {
            let menuBtnIn = getChildrenByClassName(subLevel.parentNode, menu.menuBtnInClass);

            if (menuBtnIn.length > 0) {
                menuBtnIn[0].focus();
            }

            // if primary level -> reset height of element + inner element
            if (mainLevel.classList.contains("js-drop-menu-list--main") && menu.layout === "desktop") {
                if (menu.innerElement.length > 0) {
                    menu.innerElement[0].style.height = '';
                }
            }
        });
    }

    function animateDropMenu(menu, init, final, cb) {
        let initHeight = init.offsetHeight;
        let finalHeight = final.offsetHeight;

        if (menu.innerElement.length < 1) {
            if (cb) setTimeout(function () {
                cb();
            }, menu.animationDuration * 1000);

            return;
        }

        let currentInnerElement = null;
        menu.innerElement.forEach((innerElement) => {
            if (init.closest(".js-drop-menu-inner") === innerElement) {
                currentInnerElement = innerElement;
            }
        });

        // make sure init and final height are smaller than max height
        if (initHeight > menu.maxHeight) {
            initHeight = menu.maxHeight;
        }

        if (finalHeight > menu.maxHeight) {
            finalHeight = menu.maxHeight;
        }

        let change = finalHeight - initHeight;
        let currentTime = null;
        let duration = menu.animationDuration * 1000;

        let animateHeight = function (timestamp) {
            if (!currentTime) {
                currentTime = timestamp;
            }

            let progress = timestamp - currentTime;

            if (progress > duration) {
                progress = duration;
            }

            let val = easeOutQuart(progress, initHeight, change, duration);
            currentInnerElement.style.height = val + "px";

            if (progress < duration) {
                window.requestAnimationFrame(animateHeight);
            } else {
                currentInnerElement.style.height = finalHeight + "px";
                if (cb) {
                    cb();
                }
            }
        };

        //set the height of the element before starting animation -> fix bug on Safari
        currentInnerElement.style.height = initHeight + "px";
        window.requestAnimationFrame(animateHeight);
    }

    function resetAllLevels(menu) {
        let openLevels = menu.element.getElementsByClassName(menu.levelInClass);
        let closeLevels = menu.element.getElementsByClassName(menu.levelOutClass);

        while (openLevels[0]) {
            openLevels[0].classList.remove(menu.levelInClass);
        }

        while (closeLevels[0]) {
            closeLevels[0].classList.remove(menu.levelOutClass);
        }
    }

    function focusFirstElement(menu, level) {
        let element = getFirstFocusable(level);

        if (element) {
            element.focus();
        }
    }

    function getFirstFocusable(element) {
        let elements = element.querySelectorAll(focusableElString);

        for (let i = 0; i < elements.length; i++) {
            if (elements[i].offsetWidth || elements[i].offsetHeight || elements[i].getClientRects().length) {
                return elements[i];
            }
        }
    }

    function getFocusableElements(menu) {
        // all visible focusable elements
        let elements = menu.element.querySelectorAll(focusableElString);

        menu.focusableElements = [];
        for (let i = 0; i < elements.length; i++) {
            if (isVisible(menu, elements[i])) {
                menu.focusableElements.push(elements[i]);
            }
        }
    }

    function isVisible(menu, element) {
        let elementVisible = false;
        if (
            (element.offsetWidth || element.offsetHeight || element.getClientRects().length)
            && getComputedStyle(element).getPropertyValue("visibility") === "visible"
        ) {
            elementVisible = true;
            if (menu.element.contains(element) && element.parentNode) {
                elementVisible = isVisible(menu, element.parentNode);
            }
        }

        return elementVisible;
    }

    function resetAutocomplete(menu) {
        if (menu.autocompleteInput && menu.autocompleteInput.length > 0) {
            menu.autocompleteInput[0].value = '';
        }
    }

    function setDropMenuMaxHeight(menu) {
        if (!menu.trigger) {
            return;
        }

        if (menu.layout === "mobile") {
            menu.maxHeight = "100%";
            menu.element.style.maxHeight = menu.maxHeight;

            if (menu.autocompleteList.length > 0) {
                menu.autocompleteList[0].style.maxHeight = `calc(100% - ${menu.autocompleteInput[0].offsetHeight}px)`;
            }
        } else {
            menu.maxHeight = window.innerHeight - menu.trigger.getBoundingClientRect().bottom - menu.verticalGap - 15;
            menu.element.style.maxHeight = menu.maxHeight + "px";

            if (menu.autocompleteList.length > 0) {
                menu.autocompleteList[0].style.maxHeight = (menu.maxHeight - menu.autocompleteInput[0].offsetHeight) + 'px';
            }
        }
    }

    function getLayoutValue(menu) {
        menu.layout = getComputedStyle(menu.element, ":before").getPropertyValue("content").replace(/['"]/g, "");
    }

    function placeDropMenu(menu) {
        let top = (parseInt(menu.trigger.getBoundingClientRect().bottom) + menu.verticalGap) + 'px';
        menu.element.style.top = top;

        if (menu.layout === "mobile") {
            menu.element.style.paddingBottom = top;
        }
    }

    function closeOnResize(menu) {
        getLayoutValue(menu);

        if (menu.layout === "mobile" || !dropMenuVisible(menu)) {
            return;
        }

        menu.trigger.click();
    }

    function closeOnClick(menu, target) {
        if (menu.layout === "mobile" || !dropMenuVisible(menu)) {
            return;
        }

        if (menu.element.contains(target) || menu.trigger.contains(target)) {
            return;
        }

        menu.trigger.click();
    }

    function initFocusOut(menu) {
        menu.element.addEventListener("keydown", function (event) {
            if (event.keyCode && event.keyCode === 9 || event.key && event.key === 'Tab') {
                getFocusableElements(menu);

                if (
                    (menu.focusableElements[0] === document.activeElement && event.shiftKey)
                    || (menu.focusableElements[menu.focusableElements.length - 1] === document.activeElement && !event.shiftKey)
                ) {
                    menu.trigger.click();
                }
            } else if (event.keyCode && event.keyCode === 27 || event.key && event.key.toLowerCase() === 'escape' && dropMenuVisible(menu)) {
                menu.trigger.click();
            }
        });
    }

    // init DropMenu objects
    let dropMenus = document.getElementsByClassName("js-drop-menu");
    let focusableElString = "[href], input:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex=\"-1\"]), [contenteditable], summary";

    if (dropMenus.length > 0) {
        let dropMenusArray = [];

        for (let i = 0; i < dropMenus.length; i++) {
            dropMenusArray.push(new DropMenu(dropMenus[i]));
        }

        // on resize -> close all drop menu elements
        window.addEventListener("resize", function () {
            dropMenusArray.forEach(function (element) {
                closeOnResize(element);
            });
        });

        // close drop menu when clicking outside it
        window.addEventListener("click", function (event) {
            dropMenusArray.forEach(function (element) {
                closeOnClick(element, event.target);
            });
        });
    }
};
